import { type ApolloError, gql, useQuery } from '@apollo/react-hooks';
import { type HodDoSAssessment } from './types';
import { HOD_DOS_DECISION } from '../../../HoDDoSAssessment';
import { type ApplicationRemarkType } from '../../../../Nomination/graphql';

type GetHoDDoSAssessment = {
  hodDosAssessment: HodDoSAssessment | null;
  hodRequired: boolean;
  remarks: ApplicationRemarkType[];
  loading: boolean;
  error: ApolloError | undefined;
  refetch: () => void;
};

export const useGetHoDDoSAssessment = (
  applicationId: string,
): GetHoDDoSAssessment => {
  const { data, loading, error, refetch } = useQuery(
    gql`
      query ($input: AssessmentFilter!, $id: ID!) {
        application(id: $id) {
          remarks {
            fromRole
            toRole
            remark
            createdAt
          }
        }
        hodDosAssessment(input: $input) {
          applicationId
          externalGrantOrDEA {
            required
            confirmation
            accountCode
          }
          nonSTEMOrNonSHS {
            required
            confirmation
            accountCode
            assumptionDate
          }
          competitiveFund {
            required
            confirmation
            accountCode
          }
          dualPhD {
            required
            confirmation
            accountCode
          }
          decision
          remarks
          submittedAt
        }
      }
    `,
    {
      variables: { input: { applicationId }, id: applicationId },
      fetchPolicy: 'network-only',
      skip: !applicationId,
    },
  );

  let hodDosAssessment: HodDoSAssessment | null = {
    externalGrantOrDEA: {
      required: false,
      confirmation: false,
      accountCode: '',
    },
    nonSTEMOrNonSHS: {
      required: false,
      confirmation: false,
      accountCode: '',
      assumptionDate: '',
    },
    competitiveFund: {
      required: false,
      confirmation: false,
      accountCode: '',
    },
    dualPhD: {
      required: false,
      confirmation: false,
      accountCode: '',
    },
    decision: HOD_DOS_DECISION.NA,
    remarks: '',
    submitted: false,
  };

  let hodRequired = false;

  if (!loading) {
    if (
      data?.hodDosAssessment !== null &&
      data?.hodDosAssessment !== undefined
    ) {
      const {
        externalGrantOrDEA,
        nonSTEMOrNonSHS,
        competitiveFund,
        dualPhD,
        decision,
        remarks,
        submittedAt,
      } = data?.hodDosAssessment ?? {};
      const externalRequired: boolean = externalGrantOrDEA?.required ?? false;
      const nonSTEMRequired: boolean = nonSTEMOrNonSHS?.required ?? false;
      const competitiveFundRequired: boolean =
        competitiveFund?.required ?? false;
      const dualPhDRequired: boolean = dualPhD?.required ?? false;

      hodRequired =
        externalRequired ||
        nonSTEMRequired ||
        competitiveFundRequired ||
        dualPhDRequired;
      hodDosAssessment = {
        externalGrantOrDEA: {
          required: externalRequired,
          confirmation: externalGrantOrDEA?.confirmation ?? false,
          accountCode: externalGrantOrDEA?.accountCode ?? '',
        },
        nonSTEMOrNonSHS: {
          required: nonSTEMRequired,
          confirmation: nonSTEMOrNonSHS?.confirmation ?? false,
          assumptionDate: nonSTEMOrNonSHS?.assumptionDate,
          accountCode: nonSTEMOrNonSHS?.accountCode ?? '',
        },
        competitiveFund: {
          required: competitiveFundRequired,
          confirmation: competitiveFund?.confirmation ?? false,
          accountCode: competitiveFund?.accountCode ?? '',
        },
        dualPhD: {
          required: dualPhDRequired,
          confirmation: dualPhD?.confirmation ?? false,
          accountCode: dualPhD?.accountCode ?? '',
        },
        decision:
          decision === null
            ? HOD_DOS_DECISION.NA
            : (decision as HOD_DOS_DECISION),
        remarks: remarks ?? '',
        submitted: submittedAt ?? false,
      };
    } else hodDosAssessment = null;
  }

  const remarks: ApplicationRemarkType[] = data?.application?.remarks ?? [];

  return { hodDosAssessment, hodRequired, remarks, loading, error, refetch };
};
